/**
 * MFPortfolioValidation — Phase F
 *
 * Shows the user's held MFs ranked by recommendation (worst first):
 *   • Identity: fund name, AMC, category
 *   • Absolute: composite score, confidence, dataQuality
 *   • Relative: rank #X of Y in subCategory, percentile
 *   • Comparison: fund CAGR vs category median, with excess colour-coded
 *   • Recommendation badge: HOLD / WATCH / REDUCE / REPLACE
 *   • Expandable evidence — typed positive/neutral/negative/summary bullets
 */

import React, { useState, useEffect, useMemo } from 'react'
import {
  Briefcase, TrendingUp, TrendingDown, Shield, AlertTriangle,
  ChevronDown, ChevronRight, RefreshCw, Filter, BarChart2,
} from 'lucide-react'
import { mfIntelAPI } from '../services/api'
import LoadingSpinner from './LoadingSpinner'
import MFExplainPanel from './MFExplainPanel'

const fmt = (v, d = 2) => v != null && !isNaN(v) ? Number(v).toFixed(d) : '—'
const signed = (v, d = 2) => v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(d)}`

const RecBadge = ({ rec }) => {
  const styles = {
    HOLD:    'bg-success-100 text-success-700 border-success-200',
    WATCH:   'bg-amber-100   text-amber-700   border-amber-200',
    REDUCE:  'bg-orange-100  text-orange-700  border-orange-200',
    REPLACE: 'bg-danger-100  text-danger-700  border-danger-200',
  }
  return (
    <span className={`text-xs font-bold px-2 py-1 rounded-md border uppercase tracking-wide ${styles[rec] || styles.WATCH}`}>
      {rec}
    </span>
  )
}

const ConfBadge = ({ level }) => {
  const s = { HIGH: 'bg-success-100 text-success-700', MEDIUM: 'bg-amber-100 text-amber-700', LOW: 'bg-danger-100 text-danger-700' }
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${s[level] || s.LOW}`}>{level}</span>
}

const ScoreBar = ({ score }) => {
  const s = score ?? 0
  const color = s >= 65 ? 'bg-success-500' : s >= 50 ? 'bg-amber-400' : s >= 40 ? 'bg-orange-400' : 'bg-danger-400'
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${s}%` }} />
      </div>
      <span className="text-xs font-bold tabular-nums">{fmt(s, 1)}</span>
    </div>
  )
}

const EvidenceBullet = ({ bullet }) => {
  const styles = {
    positive: { icon: '✓', cls: 'text-success-700 bg-success-50/50' },
    neutral:  { icon: '•', cls: 'text-gray-700   bg-gray-50/70' },
    negative: { icon: '✗', cls: 'text-danger-700 bg-danger-50/50' },
    summary:  { icon: '→', cls: 'text-primary-700 bg-primary-50 font-medium border-l-2 border-primary-400' },
  }
  const s = styles[bullet.type] || styles.neutral
  return (
    <div className={`flex items-start gap-2 text-xs px-2 py-1.5 rounded ${s.cls}`}>
      <span className="font-bold mt-0.5 shrink-0">{s.icon}</span>
      <span>{bullet.text}</span>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

const MFPortfolioValidation = () => {
  const [data, setData]       = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [filter, setFilter]   = useState('all')   // all | actionable | hold
  const [expanded, setExpanded] = useState({})    // schemeCode → 'evidence' | 'explain' | null
  const [explainData, setExplainData] = useState({})  // schemeCode → explain doc

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await mfIntelAPI.portfolioValidate()
        setData(res.data.holdings || [])
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load portfolio validation')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const filtered = useMemo(() => {
    if (filter === 'actionable') return data.filter(h => ['REPLACE', 'REDUCE'].includes(h.recommendation))
    if (filter === 'hold')       return data.filter(h => h.recommendation === 'HOLD')
    return data
  }, [data, filter])

  // Summary counts by recommendation
  const counts = useMemo(() => {
    const c = { HOLD: 0, WATCH: 0, REDUCE: 0, REPLACE: 0 }
    for (const h of data) c[h.recommendation] = (c[h.recommendation] || 0) + 1
    return c
  }, [data])

  const toggleExpand = async (code, section) => {
    const current = expanded[code]
    const next    = current === section ? null : section
    setExpanded(prev => ({ ...prev, [code]: next }))

    if (next === 'explain' && !explainData[code]) {
      try {
        const res = await mfIntelAPI.explainScheme(code)
        setExplainData(prev => ({ ...prev, [code]: res.data }))
      } catch {
        setExplainData(prev => ({ ...prev, [code]: { error: 'Failed to load explain data' } }))
      }
    }
  }

  if (loading) return <div className="flex items-center justify-center py-16"><LoadingSpinner size="large" /></div>
  if (error)   return <div className="text-center py-16 text-danger-600 text-sm">{error}</div>

  return (
    <div className="space-y-4">

      {/* ── Summary strip ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { key: 'HOLD',    label: 'Hold',    color: 'bg-success-50 text-success-700 border-success-200', count: counts.HOLD },
          { key: 'WATCH',   label: 'Watch',   color: 'bg-amber-50   text-amber-700   border-amber-200',   count: counts.WATCH },
          { key: 'REDUCE',  label: 'Reduce',  color: 'bg-orange-50  text-orange-700  border-orange-200',  count: counts.REDUCE },
          { key: 'REPLACE', label: 'Replace', color: 'bg-danger-50  text-danger-700  border-danger-200',  count: counts.REPLACE },
        ].map(({ key, label, color, count }) => (
          <div key={key} className={`border rounded-xl p-3 ${color}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{label}</p>
            <p className="text-2xl font-bold">{count}</p>
            <p className="text-[10px] opacity-60">{count === 1 ? 'fund' : 'funds'}</p>
          </div>
        ))}
      </div>

      {/* ── Filter pills ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-gray-400" />
        {[
          { id: 'all',        label: `All (${data.length})` },
          { id: 'actionable', label: `Actionable (${counts.REPLACE + counts.REDUCE})` },
          { id: 'hold',       label: `Hold (${counts.HOLD})` },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
              filter === f.id ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Holdings list ─────────────────────────────────────────────────── */}
      {!filtered.length ? (
        <div className="text-center py-12 text-gray-400 text-sm border border-dashed rounded-xl">
          {data.length === 0
            ? 'No active MF holdings found. Add transactions in the Holdings tab.'
            : 'No funds match the current filter.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(h => {
            const exp = expanded[h.schemeCode]
            return (
              <div key={h.schemeCode} className="border border-gray-200 rounded-xl overflow-hidden bg-white">

                {/* Top row */}
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 px-4 py-3 items-center">

                  {/* Identity + score */}
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-gray-900 truncate">{h.fundName || h.schemeName}</p>
                        <ConfBadge level={h.confidence} />
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {h.amc} · {h.subCategory || h.category}
                        {h.rank && h.categorySize && (
                          <span className="ml-2 font-medium">
                            #{h.rank} of {h.categorySize}
                            <span className="text-gray-400"> ({Math.round(h.percentile ?? 0)}p)</span>
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="flex flex-wrap gap-4 text-xs">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase">Score</p>
                      <ScoreBar score={h.compositeScore} />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase">3Y CAGR</p>
                      <p className="font-semibold tabular-nums">
                        <span className={h.cagr3Y != null && h.cagr3Y >= 0 ? 'text-success-600' : 'text-danger-600'}>
                          {h.cagr3Y != null ? `${fmt(h.cagr3Y)}%` : '—'}
                        </span>
                        {h.excessCagr3Y != null && (
                          <span className={`ml-1 text-[10px] ${h.excessCagr3Y >= 0 ? 'text-success-500' : 'text-danger-500'}`}>
                            ({signed(h.excessCagr3Y)})
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase">Sharpe</p>
                      <p className="font-semibold tabular-nums">
                        {fmt(h.sharpeRatio, 3)}
                        {h.excessSharpe != null && (
                          <span className={`ml-1 text-[10px] ${h.excessSharpe >= 0 ? 'text-success-500' : 'text-danger-500'}`}>
                            ({signed(h.excessSharpe, 3)})
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase">Max DD</p>
                      <p className="font-semibold tabular-nums">
                        {h.maxDrawdown != null ? `${fmt(h.maxDrawdown)}%` : '—'}
                      </p>
                    </div>
                  </div>

                  {/* Recommendation + actions */}
                  <div className="flex items-center gap-2 justify-end">
                    <RecBadge rec={h.recommendation} />
                    <button
                      onClick={() => toggleExpand(h.schemeCode, 'evidence')}
                      className="text-[11px] text-gray-500 hover:text-primary-600 underline-offset-2 hover:underline"
                    >
                      {exp === 'evidence' ? 'Hide' : 'Why?'}
                    </button>
                    <button
                      onClick={() => toggleExpand(h.schemeCode, 'explain')}
                      className="text-[11px] text-gray-500 hover:text-primary-600 underline-offset-2 hover:underline flex items-center gap-1"
                    >
                      <BarChart2 className="w-3 h-3" />
                      Full
                    </button>
                  </div>
                </div>

                {/* Evidence panel */}
                {exp === 'evidence' && (
                  <div className="bg-gray-50/60 border-t border-gray-200 px-4 py-3 space-y-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
                      Evidence for {h.recommendation}
                    </p>
                    {(h.evidence || []).map((b, i) => (
                      <EvidenceBullet key={i} bullet={b} />
                    ))}
                  </div>
                )}

                {/* Full explain panel */}
                {exp === 'explain' && (
                  <div className="bg-gray-50/60 border-t border-gray-200 px-4 py-3">
                    {!explainData[h.schemeCode] ? (
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <RefreshCw className="w-3 h-3 animate-spin" /> Loading explain data…
                      </div>
                    ) : explainData[h.schemeCode].error ? (
                      <p className="text-xs text-danger-500">{explainData[h.schemeCode].error}</p>
                    ) : (
                      <MFExplainPanel data={explainData[h.schemeCode]} compact />
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default MFPortfolioValidation
